import type { ReactNode } from 'react';
import { cn } from '@/lib/utils';
import { onboardingPageClass } from '@/components/smartai/ComponentOnboarding/onboarding-layout';
import { buildDiffCardClass } from '@/components/smartai/ComponentBuildDiff/build-diff-layout';
import type { FolderAccent } from './scriptPathTree';

export const testCasesPageClass = onboardingPageClass;

export const testCasesCardClass = cn(
  buildDiffCardClass,
  'flex min-h-[min(72vh,760px)] flex-1 flex-col overflow-hidden',
);

export const testCasesCardChildClass = 'min-h-0 flex-1';

export const testCasesEmptyStateClass =
  'flex flex-col items-center justify-center gap-4 p-12 text-center text-sm leading-6 text-[var(--inv-text-secondary)]';

export const testCasesTabRootClass =
  'flex min-h-0 flex-1 flex-col overflow-hidden bg-[var(--inv-surface)]';

export const testCasesToolbarClass =
  'flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-2.5 text-[0.8rem] text-slate-600';

export const testCasesMetaClass = '[&_code]:text-[0.7rem]';

export const testCasesActionsClass =
  'flex shrink-0 flex-wrap items-center gap-3';

export const testCasesPagerClass = 'flex items-center gap-2';

export const testCasesPageLabelClass =
  'whitespace-nowrap text-[0.75rem] text-[var(--inv-text-muted)]';

export const testCasesToolbarButtonClass = 'h-9 shadow-none';

export const testCasesMainClass =
  'flex min-h-0 flex-1 overflow-hidden';

export const testCasesScriptsHeaderClass =
  'flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-[var(--inv-border)] px-5 py-4';

export const testCasesScriptsTitleClass =
  'm-0 text-[0.6875rem] font-bold uppercase tracking-[0.14em] text-[var(--inv-text-muted)]';

export const testCasesScriptsCountClass =
  'ml-2 inline-flex min-w-[1.5rem] items-center justify-center rounded-full bg-[var(--inv-surface-muted)] px-2 py-0.5 text-[0.6875rem] font-semibold tabular-nums text-[var(--inv-text-secondary)]';

export const testCasesScriptsListClass = 'px-5 py-4';

export const testCasesScriptsScrollClass = 'min-h-0 flex-1 overflow-auto';

export const testCasesScriptCardClass =
  'mb-3 flex cursor-pointer appearance-none flex-wrap items-center gap-4 rounded-[var(--inv-radius-control,12px)] border border-solid border-[var(--inv-border)] bg-[var(--inv-surface)] px-4 py-3.5 font-inherit text-inherit shadow-[var(--inv-shadow,0_1px_2px_rgba(43,33,24,0.04))] transition-[border-color,box-shadow,background-color] hover:border-[var(--inv-border-strong)] hover:bg-[var(--inv-surface-muted)] hover:shadow-[0_2px_8px_rgba(43,33,24,0.06)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--inv-accent-ring)]';

export const testCasesScriptCardSelectedClass =
  'border-[var(--inv-accent)] bg-[var(--inv-accent-soft)] shadow-[0_2px_8px_rgba(var(--inv-accent-rgb,0,70,184),0.12)] ring-1 ring-[var(--inv-accent-ring)]';

export const testCasesScriptIconWrapClass =
  'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[var(--inv-accent-soft)] text-[var(--inv-accent)]';

export const testCasesScriptMainClass = 'min-w-[220px] flex-1';

export const testCasesScriptNameClass =
  'm-0 truncate text-[0.9375rem] font-semibold text-[var(--inv-text)]';

export const testCasesScriptMetaClass =
  'mt-1 truncate text-[0.75rem] text-[var(--inv-text-muted)]';

export const testCasesScriptProgressTrackClass =
  'mt-2.5 h-1.5 w-full overflow-hidden rounded-full bg-slate-100';

export function testCasesScriptProgressBarClass(pct: number): string {
  return cn(
    'h-full rounded-full transition-all',
    pct >= 80 && 'bg-emerald-500',
    pct >= 40 && pct < 80 && 'bg-amber-500',
    pct > 0 && pct < 40 && 'bg-red-500',
    pct <= 0 && 'bg-slate-200',
  );
}

export const testCasesScriptStatsClass =
  'mt-1 text-[0.6875rem] text-slate-500';

export const testCasesScriptCoverageClass =
  'min-w-[4.5rem] shrink-0 text-center';

export function testCasesScriptCoverageValueClass(pct: number | null): string {
  return cn(
    'text-[1.375rem] font-bold leading-none tabular-nums',
    pct == null && 'text-slate-300',
    pct != null && pct >= 80 && 'text-emerald-600',
    pct != null && pct >= 40 && pct < 80 && 'text-amber-600',
    pct != null && pct < 40 && 'text-red-600',
  );
}

export const testCasesScriptCoverageLabelClass =
  'mt-1 text-[0.625rem] uppercase tracking-wide text-[var(--inv-text-muted)]';

export function testCasesScriptStatusBadgeClass(
  variant: 'active' | 'removed' | 'covered' | 'partial' | 'uncovered',
): string {
  return cn(
    'shrink-0 rounded-full px-2.5 py-1 text-[0.6875rem] font-semibold',
    variant === 'active' && 'bg-slate-100 text-slate-600',
    variant === 'removed' && 'bg-red-50 text-red-700',
    variant === 'covered' && 'bg-emerald-50 text-emerald-700',
    variant === 'partial' && 'bg-amber-50 text-amber-700',
    variant === 'uncovered' && 'bg-orange-50 text-orange-700',
  );
}

export const testCasesTreeWrapClass =
  'relative flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden bg-[var(--inv-bg)]';

export const testCasesRefreshingClass =
  'absolute right-4 top-3 z-[1] rounded-full border border-slate-200/80 bg-white/90 px-2.5 py-1 text-[0.7rem] text-slate-500 shadow-sm backdrop-blur-sm';

export const testCasesEmptyMessageClass =
  'm-0 p-7 text-center text-slate-400';

export const testCasesLoadingMsgClass =
  'mt-4 text-[0.85rem] text-slate-500';

export const testCasesErrorClass = 'mb-3 text-red-700';

export const testCasesPlaceholderClass =
  'flex flex-1 items-center justify-center overflow-auto p-8 text-center text-slate-500';

export const testCasesPlaceholderInnerClass = 'max-w-[420px] [&_h2]:m-0 [&_h2]:mb-2 [&_h2]:text-[1.1rem] [&_h2]:text-slate-900 [&_p]:m-0 [&_p]:mb-2';

export const testCasesDetailAsideClass =
  'flex w-full shrink-0 flex-col overflow-hidden border-t border-[var(--inv-border)] bg-[var(--inv-surface)]';

export const testCasesDetailHeaderClass =
  'shrink-0 border-b border-[var(--inv-border)] px-5 py-3';

export const testCasesDetailTitleClass =
  'm-0 text-[0.6875rem] font-bold uppercase tracking-[0.14em] text-[var(--inv-text-muted)]';

export const testCasesDetailNameClass =
  'mt-1 break-all text-[0.9375rem] font-semibold text-[var(--inv-text)]';

export const testCasesDetailPathClass =
  'mt-1 break-all text-[0.75rem] text-[var(--inv-text-muted)] [&_code]:text-[0.7rem]';

export const testCasesDetailBodyClass =
  'max-h-[min(40vh,380px)] overflow-auto px-5 py-4';

export const testCasesEntityListClass =
  'm-0 grid list-none grid-cols-1 gap-2 p-0 sm:grid-cols-2 lg:grid-cols-3';

export const testCasesEntityRowClass =
  'flex flex-col gap-1 rounded-[var(--inv-radius-control,12px)] border border-solid border-[var(--inv-border)] bg-[var(--inv-bg)] px-3 py-2.5 text-[0.78rem]';

export const testCasesEntityTypeClass =
  'text-[0.65rem] uppercase text-slate-500';

export const testCasesEntityFileClass =
  'max-w-full overflow-hidden text-ellipsis text-[0.65rem] text-slate-600';

export const testCasesHashDetailsClass =
  'w-full [&_code]:break-all [&_code]:text-[0.68rem] [&_summary]:cursor-pointer [&_summary]:font-semibold [&_ul]:mb-0 [&_ul]:mt-1.5 [&_ul]:max-h-32 [&_ul]:overflow-auto [&_ul]:pl-[1.1rem]';

export const testCasesHashDetailsFailedClass =
  '[&_summary]:text-amber-700';

export const testCasesHashBannerTextClass =
  'm-0 min-w-48 flex-1';

export const testCasesHashCommitClass = 'text-[0.72rem]';

export const testCasesHashBannerChangedClass =
  'font-semibold text-orange-700';

export const testCasesHashBannerAddedClass =
  'font-semibold text-sky-700';

export const testCasesHashBannerFailedClass =
  'font-semibold text-amber-700';

export const testCasesHashRecoveredClass =
  'text-[0.68rem] text-slate-500';

export const testCasesHashFailReasonClass =
  'text-[0.68rem] text-slate-500';

export const testCasesHashBannerDismissClass =
  'shrink-0 cursor-pointer border-0 bg-transparent px-1 text-[1.1rem] leading-none opacity-70 hover:opacity-100';

export const testCasesTreeClass =
  'space-y-0.5 px-2 py-3 text-[0.8125rem]';

export const testCasesTreeBranchClass =
  'ml-3 border-l border-slate-200/90';

export const testCasesTreeNestClass = 'space-y-0.5 pb-1 pl-0';

const folderAccentStripe: Record<FolderAccent, string> = {
  p1: 'border-l-red-500',
  p2: 'border-l-orange-500',
  p3: 'border-l-violet-500',
  neutral: 'border-l-transparent',
};

const folderAccentIcon: Record<FolderAccent, string> = {
  p1: 'text-red-600',
  p2: 'text-orange-600',
  p3: 'text-violet-600',
  neutral: 'text-slate-500',
};

const folderAccentTitle: Record<FolderAccent, string> = {
  p1: 'text-xs font-semibold uppercase tracking-wide text-red-800',
  p2: 'text-xs font-semibold uppercase tracking-wide text-orange-800',
  p3: 'text-xs font-semibold text-violet-800',
  neutral: 'font-medium text-slate-700',
};

export function folderAccentIconClass(accent: FolderAccent): string {
  return folderAccentIcon[accent];
}

export function folderRowClass(
  accent: FolderAccent,
  options: {
    isLeafTestCase: boolean;
    isLeafFolder: boolean;
    selected: boolean;
  },
): string {
  return cn(
    'group/row relative mx-0.5 flex w-[calc(100%-4px)] cursor-pointer items-center gap-1 rounded-lg border-0 border-l-[3px] border-solid py-1.5 pr-2.5 text-left transition-all duration-150',
    'hover:bg-slate-100/90',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--inv-accent,#0ea5e9)]/35',
    folderAccentStripe[accent],
    options.isLeafFolder && 'cursor-default',
    options.isLeafTestCase &&
      cn(
        'border-l-sky-500 hover:bg-sky-50/80',
        options.selected
          ? 'bg-[var(--inv-accent-soft,#e0f2fe)] font-medium text-[var(--inv-text,#0f172a)] shadow-sm ring-1 ring-sky-500/25'
          : 'text-slate-700',
      ),
    !options.isLeafTestCase &&
      options.selected &&
      'bg-slate-100 font-medium ring-1 ring-slate-300/60',
    !options.isLeafTestCase && !options.selected && 'text-slate-700',
  );
}

export function folderTitleClass(accent: FolderAccent): string {
  return cn(
    'inline-flex min-w-0 flex-1 flex-wrap items-center gap-2 truncate',
    folderAccentTitle[accent],
  );
}

export function folderCountClass(accent: FolderAccent): string {
  return cn(
    'shrink-0 rounded-full px-2 py-0.5 text-[0.68rem] font-semibold tabular-nums',
    accent === 'p1' && 'bg-red-100 text-red-700',
    accent === 'p2' && 'bg-orange-100 text-orange-700',
    accent === 'p3' && 'bg-violet-100 text-violet-700',
    accent === 'neutral' && 'bg-slate-100 text-slate-600',
  );
}

export function folderCaretButtonClass(): string {
  return cn(
    'flex h-6 w-6 shrink-0 items-center justify-center rounded-md border-0 bg-transparent p-0',
    'text-slate-400 transition-colors group-hover/row:text-slate-600',
    'hover:bg-slate-200/70 disabled:cursor-default disabled:opacity-30',
  );
}

export function folderCaretIconClass(expanded: boolean): string {
  return cn(
    'h-3.5 w-3.5 shrink-0 transition-transform duration-200',
    expanded && 'rotate-90',
  );
}

export type HashBannerVariant = 'default' | 'error' | 'warn' | 'info';

export function hashBannerClass(variant: HashBannerVariant): string {
  return cn(
    'relative flex shrink-0 flex-wrap items-start gap-x-4 gap-y-2 border-b px-4 py-2 text-[0.78rem]',
    variant === 'default' && 'border-emerald-200 bg-emerald-50 text-emerald-900',
    variant === 'error' && 'border-red-200 bg-red-50 text-red-800',
    variant === 'warn' && 'border-amber-200 bg-amber-50 text-amber-900',
    variant === 'info' && 'border-blue-200 bg-blue-50 text-blue-900',
  );
}

export function TestCasesPlaceholder({
  children,
  inner = false,
}: {
  children: ReactNode;
  inner?: boolean;
}) {
  return (
    <div className={testCasesPlaceholderClass}>
      {inner ? (
        <div className={testCasesPlaceholderInnerClass}>{children}</div>
      ) : (
        children
      )}
    </div>
  );
}
